import { forwardRef } from 'react';

import { type ColorScheme, gray, VARIANT_MAP } from '../color';

import styled, { css } from 'styled-components';
import { defaultTheme } from '@/theme';

type IconButtonColorScheme = Omit<ColorScheme, 'text'>;

const SIZE_MAP = {
  xs: {
    side: 20,
    iconSide: 12,
  },
  sm: {
    side: 28,
    iconSide: 12,
  },
  md: {
    side: 32,
    iconSide: 16,
  },
  lg: {
    side: 40,
    iconSide: 20,
  },
  xl: {
    side: 48,
    iconSide: 24,
  },
  xxl: {
    side: 56,
    iconSide: 34,
  },
} as const;

type Size = keyof typeof SIZE_MAP;

const GRAY_DARK_TRANSPARENT: IconButtonColorScheme = {
  backgroundColor: {
    default: 'transparent',
    hover: defaultTheme.createRGBAWithHex(gray[1], 0.1),
    pressed: defaultTheme.createRGBAWithHex(gray[1], 0.15),
    disabled: 'transparent',
  },
  border: {
    default: 'transparent',
    disabled: 'transparent',
  },
};

const GRAY_FLOATING: IconButtonColorScheme = {
  backgroundColor: {
    default: gray[1],
    hover: gray[2],
    pressed: gray[3],
    disabled: gray[1],
  },
  border: {
    default: 'transparent',
    disabled: 'transparent',
  },
};

type Variant =
  | 'blue-filled'
  | 'red-filled'
  | 'blue-outlined'
  | 'gray-outlined'
  | 'red-outlined'
  | 'blue-transparent'
  | 'gray-transparent'
  | 'gray-dark-transparent'
  | 'red-transparent'
  | 'gray-floating';

const ICON_BUTTON_VARIANT_MAP = {
  ...VARIANT_MAP,
  'gray-dark-transparent': GRAY_DARK_TRANSPARENT,
  'gray-floating': GRAY_FLOATING,
} as const;

type IconButtonProps = {
  variant: Variant;
  size: Size;
  renderIcon: ({ width, height }: { width: number; height: number }) => React.ReactNode;
  ariaLabel: string;
  isActive?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'>;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant, size, renderIcon, ariaLabel, isActive, ...props }, ref) => {
    return (
      <Container ref={ref} $size={size} $variant={variant} $isActive={isActive} aria-label={ariaLabel} {...props}>
        {renderIcon({ width: SIZE_MAP[size]['iconSide'], height: SIZE_MAP[size]['iconSide'] })}
      </Container>
    );
  }
);

const Container = styled.button<{ $size: Size; $variant: Variant; $isActive: boolean | undefined }>`
  width: ${({ $size }) => SIZE_MAP[$size]['side']}px;
  height: ${({ $size }) => SIZE_MAP[$size]['side']}px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  border: 0;
  padding: 0;
  box-shadow: ${({ $variant }) =>
    ICON_BUTTON_VARIANT_MAP[$variant]['border']
      ? `inset 0 0 0 1px ${ICON_BUTTON_VARIANT_MAP[$variant]['border']['default']}`
      : undefined};
  background-color: ${({ $isActive, $variant }) =>
    $isActive
      ? ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['pressed']
      : ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['default']};
  &:hover {
    background-color: ${({ $isActive, $variant }) =>
      $isActive
        ? ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['pressed']
        : ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['hover']};
  }
  &:active,
  &:focus-visible {
    background-color: ${({ $variant }) => ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['pressed']};
  }
  &:disabled {
    background-color: ${({ $variant }) => ICON_BUTTON_VARIANT_MAP[$variant]['backgroundColor']['disabled']};
    box-shadow: ${({ $variant }) =>
      ICON_BUTTON_VARIANT_MAP[$variant]['border']
        ? `inset 0 0 0 1px ${ICON_BUTTON_VARIANT_MAP[$variant]['border']['disabled']}`
        : undefined};
    cursor: not-allowed;
  }
  ${({ $variant }) =>
    $variant === 'gray-floating' &&
    css`
      border-radius: 1000px;
      box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.15);
    `}
  flex-shrink: 0;
`;
